import { useQuery } from '@tanstack/react-query';

import { createOrderV2Client } from '../api/client';
import { orderV2QueryKeys } from '../api/queryKeys';
import { RateRangeV2 } from '../api/types';

// Per-skill/address market range for markup billing accounts. Used by the
// PayRateStep's tone coloring, the ScheduleStep's below-minimum gate (on
// markup), and ReviewStep's JobCard below-minimum alert. All three sites
// previously did their own raw `useEffect` + bare fetch, so a multi-skill
// markup repeat could fan out 5+ identical `/rate_range` calls for the
// same (account, skill, address) tuple across one wizard pass — every
// step re-mount triggered a fresh request, and Review fired one per job
// card. That's the throttle/429 the QA reproduced.
//
// Routing through React Query collapses duplicate tuples to a single
// shared network call: `staleTime: Infinity` (set on the app's
// QueryClient) keeps the result cached for the session, the centralized
// `orderV2QueryKeys.rateRange` slot dedupes concurrent requests across
// step/JobCard mounts, and re-entering the wizard via "Edit shift" reuses
// the cached range instead of re-requesting.
//
// `isSettled` mirrors the old `rangeSettled` flag pattern: true once the
// fetch has succeeded OR failed (downstream gates need to distinguish
// "in-flight" from "no range applies / fetch failed").
export type UseRateRangeResult = {
  range: RateRangeV2 | null;
  isLoading: boolean;
  isError: boolean;
  isSettled: boolean;
};

const useRateRange = (
  billingAccountId: number | null,
  skillId: number | null,
  addressId: number | null,
): UseRateRangeResult => {
  const enabled =
    billingAccountId != null && skillId != null && addressId != null;

  const query = useQuery<RateRangeV2 | null>({
    queryKey: enabled
      ? orderV2QueryKeys.rateRange(
          billingAccountId as number,
          skillId as number,
          addressId as number,
        )
      : ['orders_v2', 'rate_range', 'disabled'],
    queryFn: () =>
      createOrderV2Client
        .getRateRange(
          billingAccountId as number,
          skillId as number,
          addressId as number,
        )
        .then((range) => range ?? null),
    enabled,
  });

  // When disabled (any of accountId/skillId/addressId null), `enabled`
  // gates the fetch and we report `isSettled: true` so downstream gates
  // (which use settled to distinguish in-flight from no-range-applies)
  // don't permanently block — the missing prereq is handled by other
  // upstream guards (skill/address selection on JobTypeStep, etc).
  return {
    range: query.data ?? null,
    isLoading: enabled && query.isFetching,
    isError: enabled && query.isError,
    isSettled: !enabled || query.isSuccess || query.isError,
  };
};

export default useRateRange;
